import { Vector2 } from 'three';
import {
    anteriorPosteriorPartner,
    type LumbarLevel,
} from '@/domain/anatomy/hipSpineLandmarks';
import { rebuildPostureGeometry } from '@/views/3d-planner/hipSpine/hipSpineServices';
import { applyLandmarkPositionToScene, applyFemurCircleToScene } from '@/views/3d-planner/hipSpine/scene/landmarkSceneSync';
import type { AnnotationGroup } from '@/views/3d-planner/hipSpine/types';
import { clampToImage, imagePxToNormalized, landmarkToImagePoint } from '@/views/3d-planner/hipSpine/hipSpineCoordinates';
import { type EditContext, type PostureEdits } from '@/views/3d-planner/hipSpine/xrayEdit/editState';
import {
    displaySelection,
    groupVisibilityKey,
    hjcWorld,
    isSwapped,
    resolvedFemurRadius,
    resolvedLandmarks,
    viewedSide,
} from '@/views/3d-planner/hipSpine/xrayEdit/editDerived';

// Width-normalized floor so a resize drag can't invert the circle.
const MIN_FEMUR_HEAD_RADIUS = 0.01;

export function getOrCreateEdits(ctx: EditContext, postureId: string): PostureEdits {
    let edits = ctx.editsByPosture.get(postureId);
    if (!edits) {
        edits = { landmarks: {}, femurRadius: {} };
        ctx.editsByPosture.set(postureId, edits);
    }
    return edits;
}

export function beginLandmarkDrag(
    ctx: EditContext,
    postureId: string,
    landmarkName: string,
    mouseWorld: Vector2,
): void {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return;
    const landmark = resolvedLandmarks(ctx, postureId).find(
        (candidate) => candidate.name === landmarkName,
    );
    if (!landmark) return;
    const landmarkWorld = landmarkToImagePoint(landmark, viewState.imageSize);
    ctx.dragState.value = {
        kind: 'landmark',
        postureId,
        landmarkName,
        landmarkStartWorld: landmarkWorld,
        pointerStartWorld: mouseWorld.clone(),
    };
}

export function updateLandmarkDrag(ctx: EditContext, mouseWorld: Vector2): void {
    const current = ctx.dragState.value;
    if (current.kind !== 'landmark') return;
    const viewState = ctx.store.viewState(current.postureId);
    if (!viewState?.imageSize) return;
    // The dot tracks the cursor 1:1, preserving the grab offset; the loupe
    // magnifies the same point so the cursor stays on the dot.
    const moved = mouseWorld.clone().sub(current.pointerStartWorld);
    const target = clampToImage(
        current.landmarkStartWorld.clone().add(moved),
        viewState.imageSize,
    );
    const normalized = imagePxToNormalized(target, viewState.imageSize);
    const targetName = isSwapped(ctx, current.postureId)
        ? (anteriorPosteriorPartner(current.landmarkName) ?? current.landmarkName)
        : current.landmarkName;
    const edits = getOrCreateEdits(ctx, current.postureId);
    edits.landmarks[targetName] = normalized;
}

export function beginFemurHeadRadiusResize(
    ctx: EditContext,
    postureId: string,
    head: 1 | 2,
    grabWorld: Vector2,
): void {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return;
    const center = hjcWorld(ctx, postureId, head);
    if (!center) return;
    ctx.dragState.value = {
        kind: 'femur-resize',
        postureId,
        head,
        radiusStart: resolvedFemurRadius(ctx, postureId, head),
        pointerStartDistance: grabWorld.distanceTo(center) / viewState.imageSize.width,
        pointerWorld: grabWorld.clone(),
    };
}

export function updateFemurHeadRadiusResize(ctx: EditContext, mouseWorld: Vector2): void {
    const current = ctx.dragState.value;
    if (current.kind !== 'femur-resize') return;
    const viewState = ctx.store.viewState(current.postureId);
    if (!viewState?.imageSize) return;
    const center = hjcWorld(ctx, current.postureId, current.head);
    if (!center) return;
    // Width-normalized distance; the edge tracks the cursor 1:1 so it stays under
    // the pointer while the loupe magnifies the same point.
    const currentDistance = mouseWorld.distanceTo(center) / viewState.imageSize.width;
    const radius = Math.max(
        MIN_FEMUR_HEAD_RADIUS,
        current.radiusStart + (currentDistance - current.pointerStartDistance),
    );
    current.pointerWorld.copy(mouseWorld);
    const edits = getOrCreateEdits(ctx, current.postureId);
    edits.femurRadius[current.head] = radius;
}

export function toggleGroupVisibility(
    ctx: EditContext,
    postureId: string,
    group: AnnotationGroup,
): void {
    let hidden = ctx.hiddenGroupKeysByPosture.get(postureId);
    if (!hidden) {
        hidden = new Set<string>();
        ctx.hiddenGroupKeysByPosture.set(postureId, hidden);
    }
    const key = groupVisibilityKey(group);
    if (hidden.has(key)) hidden.delete(key);
    else hidden.add(key);
}

/**
 * Recompute angles + label overlap geometry for a posture from its
 * now-stable resolved landmarks and femur radii. Passes the displayed lumbar
 * pair plus the resolved (post-edit, level-pruned) landmarks so the rebuild
 * matches what the user sees and edits. Visibility intent is re-projected by
 * the scene (which watches the rebuilt groups), so this need not touch it.
 */
export function rebuildAndSync(ctx: EditContext, postureId: string): void {
    const viewState = ctx.store.viewState(postureId);
    const posture = ctx.store.hipSpinePostures.find(
        (candidate) => candidate.posture === postureId,
    );
    if (!viewState?.imageSize) return;
    rebuildPostureGeometry(
        viewState,
        posture,
        { pair: displaySelection(ctx, postureId).pair, landmarks: resolvedLandmarks(ctx, postureId) },
        {
            1: resolvedFemurRadius(ctx, postureId, 1),
            2: resolvedFemurRadius(ctx, postureId, 2),
        },
    );
}

export function setLumbarLevel(ctx: EditContext, postureId: string, level: LumbarLevel): void {
    ctx.lumbarLevelByPosture.set(postureId, level);
    rebuildAndSync(ctx, postureId);
}

export function toggleSide(ctx: EditContext, postureId: string): void {
    ctx.viewedSideByPosture.set(
        postureId,
        viewedSide(ctx, postureId) === 'right' ? 'left' : 'right',
    );
    rebuildAndSync(ctx, postureId);
}

export function endDrag(ctx: EditContext): void {
    const finished = ctx.dragState.value;
    const postureId =
        finished.kind === 'landmark' || finished.kind === 'femur-resize'
            ? finished.postureId
            : null;

    ctx.dragState.value = { kind: 'idle' };
    ctx.hover.value = { kind: 'none' };

    if (!postureId) return;
    const viewState = ctx.store.viewState(postureId);
    if (!viewState || !viewState.imageSize) return;

    // Recompute angles + label overlap from the now-stable resolved landmarks.
    // rebuildAndSync passes resolved radii so any femur-resize edits survive.
    if (finished.kind === 'landmark') {
        rebuildAndSync(ctx, postureId);
    }
}

export function syncSceneToEdits(ctx: EditContext, postureId: string): void {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return;
    const resolved = resolvedLandmarks(ctx, postureId);
    applyLandmarkPositionToScene(viewState.annotationGroups, resolved, viewState.imageSize);
    // resolvedFemurRadius is width-normalized; scale to scene pixels.
    applyFemurCircleToScene(viewState.annotationGroups, resolved, viewState.imageSize, {
        1: resolvedFemurRadius(ctx, postureId, 1) * viewState.imageSize.width,
        2: resolvedFemurRadius(ctx, postureId, 2) * viewState.imageSize.width,
    });
}

/**
 * Sync only the femur head circle geometry to the scene.
 * Used during a femur-resize drag where landmark positions are stable —
 * skips the connecting-line rebuild that `syncSceneToEdits` would do.
 */
export function syncFemurCircleToScene(ctx: EditContext, postureId: string): void {
    const viewState = ctx.store.viewState(postureId);
    if (!viewState?.imageSize) return;
    // Width-normalized radius -> scene pixels (see syncSceneToEdits).
    const w = viewState.imageSize.width;
    applyFemurCircleToScene(viewState.annotationGroups, resolvedLandmarks(ctx, postureId), viewState.imageSize, {
        1: resolvedFemurRadius(ctx, postureId, 1) * w,
        2: resolvedFemurRadius(ctx, postureId, 2) * w,
    });
}

/**
 * Drop a posture's unsaved overlays after its save succeeded — server state (just
 * refreshed into the store) becomes authoritative. Visibility intent
 * (hiddenGroupKeysByPosture) is a view preference, NOT a saved edit, so it is kept.
 */
export function clearPostureEdits(ctx: EditContext, postureId: string): void {
    ctx.editsByPosture.delete(postureId);
    ctx.lumbarLevelByPosture.delete(postureId);
    ctx.viewedSideByPosture.delete(postureId);
}

/**
 * Drop every client-side override for a posture — unsaved landmark/femur edits,
 * the lumbar-level choice, the viewed-side toggle, and hidden-group intent — so
 * the view returns fully to the AI baseline. Used after a "Reset to AI" so no
 * stale client edit re-overlays on freshly reset landmarks.
 */
export function clearPostureOverrides(ctx: EditContext, postureId: string): void {
    ctx.editsByPosture.delete(postureId);
    ctx.lumbarLevelByPosture.delete(postureId);
    ctx.viewedSideByPosture.delete(postureId);
    ctx.hiddenGroupKeysByPosture.delete(postureId);
}

/**
 * Wire the action helpers over a shared {@link EditContext}. Each method closes
 * a module-level function over `ctx`, keeping the factory body and every action
 * within the function-length cap and the actions unit-testable in isolation.
 */
export function createEditActions(ctx: EditContext) {
    return {
        getOrCreateEdits: (postureId: string) => getOrCreateEdits(ctx, postureId),
        beginLandmarkDrag: (postureId: string, landmarkName: string, mouseWorld: Vector2) =>
            beginLandmarkDrag(ctx, postureId, landmarkName, mouseWorld),
        updateLandmarkDrag: (mouseWorld: Vector2) => updateLandmarkDrag(ctx, mouseWorld),
        beginFemurHeadRadiusResize: (postureId: string, head: 1 | 2, grabWorld: Vector2) =>
            beginFemurHeadRadiusResize(ctx, postureId, head, grabWorld),
        updateFemurHeadRadiusResize: (mouseWorld: Vector2) => updateFemurHeadRadiusResize(ctx, mouseWorld),
        toggleGroupVisibility: (postureId: string, group: AnnotationGroup) =>
            toggleGroupVisibility(ctx, postureId, group),
        setLumbarLevel: (postureId: string, level: LumbarLevel) =>
            setLumbarLevel(ctx, postureId, level),
        toggleSide: (postureId: string) => toggleSide(ctx, postureId),
        endDrag: () => endDrag(ctx),
        syncSceneToEdits: (postureId: string) => syncSceneToEdits(ctx, postureId),
        syncFemurCircleToScene: (postureId: string) => syncFemurCircleToScene(ctx, postureId),
        clearPostureEdits: (postureId: string) => clearPostureEdits(ctx, postureId),
        clearPostureOverrides: (postureId: string) => clearPostureOverrides(ctx, postureId),
        rebuildAndSync: (postureId: string) => rebuildAndSync(ctx, postureId),
    };
}
